import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getSavedAddress, saveAddress } from "../lib/api";

const AppStateContext = createContext(null);

export function AppStateProvider({ children }) {
  const [address, setAddress] = useState(() => getSavedAddress());
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dh_next_user") || "null");
    } catch {
      return null;
    }
  });
  const [addressOpen, setAddressOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [cart, setCart] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("dh_next_cart") || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(
          (line) =>
          line?.itemId &&
          line?.restaurantId &&
          line?.restaurantSlug &&
          line?.name &&
          Number(line?.price) >= 0 &&
          Number(line?.quantity) > 0,
        )
        .map((line) => ({
          ...line,
          lineId: line.lineId || `${line.itemId}:base`,
          selectedAddons: Array.isArray(line.selectedAddons) ? line.selectedAddons : [],
        }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    api("/get-settings", { method: "POST", body: "{}" })
      .then((settings) => {
        if (!Array.isArray(settings)) return;
        settings.forEach(({ key, value }) => {
          if (key === "firebasePublic" || key === "firebaseSenderId") {
            localStorage.setItem(key, value);
          }
        });
      })
      .catch(() => {});
  }, []);

  const selectAddress = useCallback((nextAddress) => {
    const saved = saveAddress(nextAddress);
    setAddress(saved);
    setAddressOpen(false);
    setLocationError("");
    return saved;
  }, []);

  useEffect(() => {
    if (!address && user?.default_address?.address) {
      selectAddress({
        lat: Number(user.default_address.latitude),
        lng: Number(user.default_address.longitude),
        address: user.default_address.address,
      });
    }
  }, [address, selectAddress, user]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation || locating) {
      if (!navigator.geolocation) setLocationError("Este dispositivo no permite obtener el GPS.");
      return;
    }

    setLocating(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const lat = Number(coords.latitude);
        const lng = Number(coords.longitude);
        let label = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        try {
          const result = await api("/coordinate-to-address", {
            method: "POST",
            body: JSON.stringify({ lat, lng }),
          });
          label = (typeof result === "string" ? result : result?.address) || label;
        } catch {
          // Las coordenadas siguen siendo válidas aunque falle el nombre de la calle.
        }

        selectAddress({ lat, lng, address: label, accuracy: Number(coords.accuracy || 0) });
        setLocating(false);
      },
      (error) => {
        setLocating(false);
        setLocationError(
          error.code === 1
            ? "Necesitamos permiso de ubicación para usar tu posición actual."
            : "No pudimos obtener tu ubicación. Intenta nuevamente.",
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }, [locating, selectAddress]);

  const updateCart = useCallback((updater) => {
    setCart((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      localStorage.setItem("dh_next_cart", JSON.stringify(next));
      return next;
    });
  }, []);

  const addToCart = useCallback((item, restaurant, selectedAddons = [], quantity = 1) => {
    updateCart((current) => {
      const belongsToAnotherStore =
        current.length > 0 && Number(current[0].restaurantId) !== Number(restaurant.id);
      const base = belongsToAnotherStore ? [] : current;
      const addonSignature = selectedAddons
        .map((addon) => Number(addon.addon_id))
        .sort((a, b) => a - b)
        .join("-");
      const lineId = `${item.id}:${addonSignature || "base"}`;
      const found = base.find((line) => line.lineId === lineId);

      if (found) {
        return base.map((line) =>
          line.lineId === lineId
            ? { ...line, quantity: Number(line.quantity) + Number(quantity) }
            : line,
        );
      }

      return [
        ...base,
        {
          itemId: item.id,
          lineId,
          restaurantId: restaurant.id,
          restaurantName: restaurant.name,
          restaurantSlug: restaurant.slug,
          name: item.name,
          image: item.image,
          price: Number(item.price || 0),
          quantity: Number(quantity),
          selectedAddons,
          isYapePromo: Number(item.is_yape_promo || item.isYapePromo || 0) === 1,
        },
      ];
    });

    window.dispatchEvent(new CustomEvent("dh:cart-added", {
      detail: {
        itemId: Number(item.id),
        name: item.name,
        quantity: Number(quantity),
      },
    }));
  }, [updateCart]);

  const changeCartQuantity = useCallback((lineId, quantity) => {
    updateCart((current) =>
      current
        .map((line) =>
          line.lineId === lineId
            ? { ...line, quantity: Math.max(0, Number(quantity)) }
            : line,
        )
        .filter((line) => line.quantity > 0),
    );
  }, [updateCart]);

  const clearCart = useCallback(() => updateCart([]), [updateCart]);

  const saveUser = useCallback((nextUser) => {
    if (nextUser) localStorage.setItem("dh_next_user", JSON.stringify(nextUser));
    else localStorage.removeItem("dh_next_user");
    setUser(nextUser);
  }, []);

  const logout = useCallback(() => saveUser(null), [saveUser]);

  const cartCount = useMemo(
    () => cart.reduce((total, line) => total + Number(line.quantity || 0), 0),
    [cart],
  );

  const value = useMemo(
    () => ({
      address,
      addToCart,
      addressOpen,
      changeCartQuantity,
      clearCart,
      cart,
      cartCount,
      locating,
      locationError,
      logout,
      openAddress: () => setAddressOpen(true),
      closeAddress: () => setAddressOpen(false),
      requestLocation,
      selectAddress,
      saveUser,
      updateCart,
      user,
    }),
    [
      address,
      addToCart,
      addressOpen,
      changeCartQuantity,
      clearCart,
      cart,
      cartCount,
      locating,
      locationError,
      logout,
      requestLocation,
      selectAddress,
      saveUser,
      updateCart,
      user,
    ],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const value = useContext(AppStateContext);
  if (!value) throw new Error("useAppState debe utilizarse dentro de AppStateProvider.");
  return value;
}
